import React, { useEffect, useMemo, useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { GripVertical } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "../../components/ui/button";
import { useDashboardLayout } from "../../hooks/useDashboardLayout";
import {
  DASHBOARD_BLOCK_REGISTRY,
  DASHBOARD_METRIC_SOURCES,
  CHART_TYPES,
  DEFAULT_DASHBOARD_LAYOUT,
} from "../../utils/dashboardPreferences";

const REGISTRY_BY_ID = DASHBOARD_BLOCK_REGISTRY.reduce((acc, b) => {
  acc[b.id] = b;
  return acc;
}, {});

const SAVE_DEBOUNCE_MS = 600;

const BlockRow = ({ id, block, label, description, onToggle }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={id}
      dragListener={false}
      dragControls={controls}
      className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3"
    >
      <button
        type="button"
        onPointerDown={(event) => controls.start(event)}
        className="cursor-grab touch-none text-gray-400 hover:text-gray-600"
        aria-label="Arrastrar para reordenar"
      >
        <GripVertical className="h-5 w-5" />
      </button>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-800">{label}</p>
        {description ? (
          <p className="text-sm text-gray-500">{description}</p>
        ) : null}
      </div>
      <Button
        size="sm"
        variant={block.visible ? "default" : "outline"}
        onClick={() => onToggle(id)}
      >
        {block.visible ? "Incluida" : "Incluir"}
      </Button>
    </Reorder.Item>
  );
};

const DashboardPersonalizationPage = () => {
  const { layout, saveLayout } = useDashboardLayout();

  const [items, setItems] = useState(layout.orderedBlocks);
  const [customCharts, setCustomCharts] = useState(layout.customCharts);

  const [title, setTitle] = useState("");
  const [metricSource, setMetricSource] = useState(
    DASHBOARD_METRIC_SOURCES[0]?.key || "",
  );
  const [chartType, setChartType] = useState("bar");
  const [maxItems, setMaxItems] = useState(8);

  const saveTimer = useRef(null);
  const lastGood = useRef({
    items: layout.orderedBlocks,
    customCharts: layout.customCharts,
  });

  useEffect(() => {
    setItems(layout.orderedBlocks);
    setCustomCharts(layout.customCharts);
    lastGood.current = {
      items: layout.orderedBlocks,
      customCharts: layout.customCharts,
    };
  }, [layout]);

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  const flush = (nextItems, nextCustom) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveLayout({
        blocks: nextItems.map((b) => ({ id: b.id, visible: b.visible })),
        customCharts: nextCustom,
      })
        .then(() => {
          lastGood.current = { items: nextItems, customCharts: nextCustom };
        })
        .catch(() => {
          toast.error("No se pudo guardar la personalización del dashboard");
          setItems(lastGood.current.items);
          setCustomCharts(lastGood.current.customCharts);
        });
    }, SAVE_DEBOUNCE_MS);
  };

  const ids = useMemo(() => items.map((b) => b.id), [items]);

  const handleReorder = (newIds) => {
    const byId = new Map(items.map((b) => [b.id, b]));
    const next = newIds.map((id) => byId.get(id)).filter(Boolean);
    setItems(next);
    flush(next, customCharts);
  };

  const toggle = (id) => {
    const next = items.map((b) =>
      b.id === id ? { ...b, visible: !b.visible } : b,
    );
    setItems(next);
    flush(next, customCharts);
  };

  const describeBlock = (block) => {
    if (!block.custom) {
      const meta = REGISTRY_BY_ID[block.id];
      return {
        label: meta?.label || block.id,
        description: meta?.description || "",
      };
    }
    const chart = customCharts.find((c) => c.id === block.id);
    return {
      label: chart ? `🧠 ${chart.title}` : "Gráfica personalizada",
      description: chart
        ? `${chart.type === "bar" ? "Barras" : "Torta"} · ${chart.source} · Top ${chart.maxItems}`
        : "",
    };
  };

  const handleCreateCustomChart = (event) => {
    event.preventDefault();
    if (!title.trim()) return;

    const source = DASHBOARD_METRIC_SOURCES.find((s) => s.key === metricSource);
    if (!source) return;

    const chart = {
      id: `custom-${Date.now()}`,
      title: title.trim(),
      type: chartType,
      source: source.key,
      metric: source.metric,
      nameKey: source.nameKey,
      maxItems: Number(maxItems) || 8,
    };

    const nextCustom = [...customCharts, chart];
    const nextItems = [
      ...items,
      { id: chart.id, family: "chart", custom: true, visible: true },
    ];
    setCustomCharts(nextCustom);
    setItems(nextItems);
    flush(nextItems, nextCustom);

    setTitle("");
    setMaxItems(8);
    setChartType("bar");
    setMetricSource(DASHBOARD_METRIC_SOURCES[0]?.key || "");
  };

  const removeCustomChart = (chartId) => {
    const nextCustom = customCharts.filter((c) => c.id !== chartId);
    const nextItems = items.filter((b) => b.id !== chartId);
    setCustomCharts(nextCustom);
    setItems(nextItems);
    flush(nextItems, nextCustom);
  };

  const resetToDefault = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveLayout(DEFAULT_DASHBOARD_LAYOUT)
      .then(() => toast.success("Configuración del dashboard restaurada"))
      .catch(() => toast.error("No se pudo restaurar la configuración"));
  };

  const visibleCount = items.filter((b) => b.visible).length;

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-5">
        <h3 className="text-lg font-semibold text-gray-800">
          Personalización del dashboard
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Arrastra para reordenar los elementos y actívalos o desactívalos según
          tu operación. Los cambios se guardan en tu cuenta y se sincronizan
          entre dispositivos.
        </p>
      </div>

      <section className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h4 className="text-base font-semibold text-gray-700">
            Elementos del dashboard ({visibleCount}/{items.length} visibles)
          </h4>
          <Button variant="outline" onClick={resetToDefault}>
            Restaurar configuración inicial
          </Button>
        </div>

        <Reorder.Group
          axis="y"
          values={ids}
          onReorder={handleReorder}
          className="space-y-2"
        >
          {items.map((block) => {
            const { label, description } = describeBlock(block);
            return (
              <BlockRow
                key={block.id}
                id={block.id}
                block={block}
                label={label}
                description={description}
                onToggle={toggle}
              />
            );
          })}
        </Reorder.Group>
      </section>

      <section className="space-y-4">
        <h4 className="text-base font-semibold text-gray-700">
          Crear nueva gráfica medible
        </h4>

        <form
          onSubmit={handleCreateCustomChart}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 border border-gray-200 rounded-xl p-4"
        >
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Título</label>
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Ej: Top 8 áreas con mayor inventario"
              className="w-full border rounded-lg px-3 py-2 text-gray-700"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Fuente de datos
            </label>
            <select
              value={metricSource}
              onChange={(event) => setMetricSource(event.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-gray-700"
            >
              {DASHBOARD_METRIC_SOURCES.map((source) => (
                <option key={source.key} value={source.key}>
                  {source.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Tipo</label>
            <select
              value={chartType}
              onChange={(event) => setChartType(event.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-gray-700"
            >
              {CHART_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Máximo de elementos
            </label>
            <input
              value={maxItems}
              onChange={(event) => setMaxItems(event.target.value)}
              type="number"
              min={3}
              max={20}
              className="w-full border rounded-lg px-3 py-2 text-gray-700"
            />
          </div>

          <div className="md:col-span-2 flex justify-end">
            <Button type="submit">Agregar al dashboard</Button>
          </div>
        </form>
      </section>

      <section className="space-y-3">
        <h4 className="text-base font-semibold text-gray-700">
          Gráficas personalizadas activas ({customCharts.length})
        </h4>

        {customCharts.length === 0 ? (
          <p className="text-sm text-gray-500">
            Aún no has creado gráficas personalizadas.
          </p>
        ) : (
          <div className="space-y-2">
            {customCharts.map((chart) => (
              <div
                key={chart.id}
                className="border border-gray-200 rounded-lg p-3 flex items-center justify-between gap-3"
              >
                <div>
                  <p className="font-medium text-gray-800">{chart.title}</p>
                  <p className="text-sm text-gray-500">
                    {chart.type === "bar" ? "Barras" : "Torta"} · Datos:{" "}
                    {chart.source}
                    {" · "}
                    Top {chart.maxItems}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => removeCustomChart(chart.id)}
                >
                  Quitar
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default DashboardPersonalizationPage;
